import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Renderer2,
    SimpleChanges,
    Inject
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatButtonModule} from '@angular/material/button';
import {MatMenuModule} from '@angular/material/menu';
import {DestinationType, GalleryType} from '../../destinations.types';
import {Observable, Subscription, finalize, Subject, takeUntil} from 'rxjs';
import {RestFactoryService} from '../../../../../core/services/rest-factory.service';
import {FuseConfirmationService} from '../../../../../../@fuse/services/confirmation';
import {NgxSpinnerService} from 'ngx-spinner';
import {MatCardModule} from "@angular/material/card";
import {MatDividerModule} from "@angular/material/divider";
import {environment} from "../../../../../../environments/environment";
import {MatDialogModule, MatDialog, MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

export interface UploadItem {
    id: string;
    file: File;
    name: string;
    previewUrl: string;
    status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
    errorMessage: string;
    subscription?: Subscription;
}

export interface UploadDialogData {
    files: File[];
    uploadFunction: (file: File) => Observable<any>;
    onSuccess: (res: any) => void;
}

@Component({
    selector: 'app-upload-queue-dialog',
    standalone: true,
    imports: [CommonModule, MatIconModule, MatButtonModule, MatTooltipModule, MatDialogModule],
    template: `
        <div class="flex flex-col h-[600px] bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
            <div class="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
                <h2 class="text-xl font-semibold m-0 dark:text-white">Upload Manager</h2>
                <button mat-icon-button (click)="closeDialog()">
                    <mat-icon svgIcon="heroicons_outline:x-circle"></mat-icon>
                </button>
            </div>

            <div class="flex flex-1 overflow-hidden">
                <div class="w-1/3 border-r dark:border-gray-700 p-4 flex flex-col items-center bg-gray-50 dark:bg-gray-900 overflow-y-auto relative">
                    <ng-container *ngIf="selectedItem">
                        <img [src]="selectedItem.previewUrl" class="max-w-full max-h-64 object-contain rounded shadow-sm mb-4">
                        <div class="text-sm font-medium break-all text-center dark:text-gray-200 mb-2">{{ selectedItem.name }}</div>

                        <div *ngIf="selectedItem.status === 'failed'" class="w-full mt-auto p-3 bg-red-100 text-red-800 rounded text-sm shadow-inner">
                            <span class="font-bold block mb-1">Upload Failed</span>
                            {{ selectedItem.errorMessage }}
                        </div>
                    </ng-container>
                </div>

                <div class="w-2/3 p-4 overflow-y-auto bg-white dark:bg-gray-800">
                    <div class="flex items-center justify-between mb-4">
                        <span class="font-medium dark:text-gray-200">Queue ({{queue.length}})</span>
                        <button mat-stroked-button color="warn" (click)="cancelAll()" [disabled]="!hasActiveUploads()">
                            Cancel Pending
                        </button>
                    </div>

                    <div class="flex flex-col gap-2">
                        <div *ngFor="let item of queue"
                             (click)="selectItem(item)"
                             class="flex items-center justify-between p-3 rounded border dark:border-gray-700 cursor-pointer transition-colors"
                             [class.bg-blue-50]="selectedItem?.id === item.id"
                             [class.dark:bg-gray-700]="selectedItem?.id === item.id"
                             [class.hover:bg-gray-50]="selectedItem?.id !== item.id"
                             [class.dark:hover:bg-gray-700]="selectedItem?.id !== item.id">

                            <div class="flex items-center gap-3 overflow-hidden">
                                <img [src]="item.previewUrl" class="w-10 h-10 object-cover rounded shadow-sm border border-gray-200">
                                <div class="flex flex-col overflow-hidden">
                                    <span class="text-sm font-medium truncate dark:text-gray-200" [title]="item.name">{{item.name}}</span>
                                    <span class="text-xs font-bold"
                                          [class.text-gray-500]="item.status === 'pending'"
                                          [class.text-blue-500]="item.status === 'processing'"
                                          [class.text-green-500]="item.status === 'completed'"
                                          [class.text-red-500]="item.status === 'failed'"
                                          [class.text-orange-500]="item.status === 'cancelled'">
                                        {{item.status | uppercase}}
                                    </span>
                                </div>
                            </div>

                            <div class="flex items-center shrink-0">
                                <button *ngIf="item.status === 'pending' || item.status === 'processing'"
                                        mat-icon-button color="warn" matTooltip="Cancel Upload"
                                        (click)="cancel(item); $event.stopPropagation()">
                                    <mat-icon svgIcon="heroicons_outline:x-circle"></mat-icon>
                                </button>
                                <button *ngIf="item.status === 'failed' || item.status === 'cancelled'"
                                        mat-icon-button color="primary" matTooltip="Retry Upload"
                                        (click)="retry(item); $event.stopPropagation()">
                                    <mat-icon svgIcon="heroicons_outline:arrow-path"></mat-icon>
                                </button>
                                <mat-icon *ngIf="item.status === 'completed'" class="text-green-500 mx-2" svgIcon="heroicons_outline:check-circle"></mat-icon>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `
})
export class UploadQueueDialogComponent implements OnInit, OnDestroy {
    queue: UploadItem[] = [];
    selectedItem: UploadItem | null = null;
    private MAX_CONCURRENT = 3;
    private activeUploads = 0;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: UploadDialogData,
        private dialogRef: MatDialogRef<UploadQueueDialogComponent>,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.queue = this.data.files.map((file, index) => ({
            id: index.toString() + Date.now().toString(),
            file: file,
            name: file.name,
            previewUrl: URL.createObjectURL(file),
            status: 'pending',
            errorMessage: ''
        }));

        if (this.queue.length > 0) {
            this.selectedItem = this.queue[0];
            this.processQueue();
        }
    }

    ngOnDestroy(): void {
        this.queue.forEach(item => {
            if (item.previewUrl) {
                URL.revokeObjectURL(item.previewUrl);
            }
            if (item.status === 'processing' && item.subscription) {
                item.subscription.unsubscribe();
            }
        });
    }

    processQueue(): void {
        while (this.activeUploads < this.MAX_CONCURRENT) {
            const next = this.queue.find(q => q.status === 'pending');
            if (!next) {
                break;
            }

            this.activeUploads++;
            next.status = 'processing';
            this.cdr.markForCheck();

            next.subscription = this.data.uploadFunction(next.file).subscribe({
                next: (res) => {
                    next.status = 'completed';
                    this.activeUploads--;
                    this.data.onSuccess(res);
                    this.processQueue();
                    this.cdr.markForCheck();
                },
                error: (err) => {
                    next.status = 'failed';
                    next.errorMessage = err?.message || err?.error?.message || 'Server rejected the file or an unknown error occurred.';
                    this.activeUploads--;
                    this.processQueue();
                    this.cdr.markForCheck();
                }
            });
        }
    }

    selectItem(item: UploadItem): void {
        this.selectedItem = item;
        this.cdr.markForCheck();
    }

    cancel(item: UploadItem): void {
        if (item.status === 'processing' && item.subscription) {
            item.subscription.unsubscribe();
            this.activeUploads--;
        }
        item.status = 'cancelled';
        this.processQueue();
        this.cdr.markForCheck();
    }

    retry(item: UploadItem): void {
        item.status = 'pending';
        item.errorMessage = '';
        this.processQueue();
        this.cdr.markForCheck();
    }

    cancelAll(): void {
        this.queue.forEach(item => {
            if (item.status === 'pending') {
                item.status = 'cancelled';
            } else if (item.status === 'processing' && item.subscription) {
                item.subscription.unsubscribe();
                item.status = 'cancelled';
                this.activeUploads--;
            }
        });
        this.cdr.markForCheck();
    }

    hasActiveUploads(): boolean {
        return this.queue.some(q => q.status === 'pending' || q.status === 'processing');
    }

    closeDialog(): void {
        this.dialogRef.close();
    }
}


@Component({
    selector: 'app-gallery-tab',
    templateUrl: './gallery-tab.component.html',
    styleUrls: ['./gallery-tab.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        MatIconModule,
        MatTooltipModule,
        MatButtonModule,
        MatMenuModule,
        MatCardModule,
        MatDividerModule,
        MatDialogModule
    ],
})
export class GalleryTabComponent implements OnInit, OnChanges, OnDestroy {
    @Input() destination: DestinationType;
    @Input() reviewMode: boolean = false;

    userUiType: number = environment.user_ui_type || 1;

    loadGallery: boolean = false;
    selectedGalleryImageIndex: number | null = null;

    isRotating: boolean = false;
    rotationDegree: number = 0;
    rotationImageBlobUrl: string | null = null;

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private API_POST_MAIN_IMAGE = "/admin/destination/destinationmainimageadd";
    private API_POST_MAIN_CARD_IMAGE = "/admin/destination/destinationcardimageadd";
    private API_POST_GALLERY_IMAGE = "/admin/destination/galleryadd";
    private API_DELETE_GALLERY_IMAGE = "/admin/destination/galleryremove";

    constructor(
        private _restFactoryService: RestFactoryService,
        private _fuseConfirmationService: FuseConfirmationService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _spinner: NgxSpinnerService,
        private _renderer: Renderer2,
        private _el: ElementRef,
        private _dialog: MatDialog
    ) {
    }

    ngOnInit(): void {
        this.initializeGallery();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.destination && changes.destination.currentValue) {
            this.initializeGallery();
        }
    }

    ngOnDestroy(): void {
        this.cleanupRotation();
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    isMissingImage(val: any): boolean {
        if (val === null || val === undefined) {
            return true;
        }
        if (typeof val === 'string') {
            const trimmed = val.trim();
            if (trimmed.length === 0) {
                return true;
            }
            if (trimmed.endsWith('content/undefined')) {
                return true;
            }
        }
        return false;
    }

    isGalleryInsufficient(): boolean {
        if (!this.destination || !this.destination.ar_gallery) {
            return true;
        }
        return this.destination.ar_gallery.length < 1;
    }

    initializeGallery(): void {
        if (!this.destination) return;
        this.selectedGalleryImageIndex = this.destination.ar_gallery && this.destination.ar_gallery.length > 0 ? 0 : null;
        this.loadGallery = true;
        this._changeDetectorRef.markForCheck();
    }

    onFileInputClick(fileUploadId: string): void {
        const fileInput = this._el.nativeElement.querySelector(`#${fileUploadId}`);
        this._renderer.selectRootElement(fileInput).click();
    }

    handleFileUpload(event: any): void {
        const input = event.target as HTMLInputElement;
        if (!input.files || input.files.length === 0) {
            input.value = '';
            return;
        }

        const files = Array.from(input.files);
        const validFiles: File[] = [];
        let hasInvalidFiles = false;

        files.forEach(file => {
            if (this.isValidImage(file)) {
                validFiles.push(file);
            } else {
                hasInvalidFiles = true;
            }
        });

        if (hasInvalidFiles) {
            this.showError("One or more files have invalid formats and were skipped. Please use .jpg, .png, .jpeg, .gif, or .webp.");
        }

        if (validFiles.length > 0) {
            this.openUploadQueueDialog(validFiles);
        }

        input.value = '';
    }

    private isValidImage(file: File): boolean {
        const allowedImageExt = ["jpg", "png", "jpeg", "gif", "webp"];
        const fileExtension = file.name.toLowerCase().split('.').pop();
        return !!fileExtension && allowedImageExt.includes(fileExtension);
    }

    private openUploadQueueDialog(files: File[]): void {
        this._dialog.open(UploadQueueDialogComponent, {
            width: '800px',
            disableClose: true,
            panelClass: 'p-0',
            data: {
                files: files,
                uploadFunction: (file: File) => {
                    const payload = new FormData();
                    payload.append("im_image", file);
                    payload.append("id_destination", this.destination.id_destination);
                    return this._restFactoryService.httpPostService(this.API_POST_GALLERY_IMAGE, payload);
                },
                onSuccess: (response: any) => {
                    const newElement: GalleryType = {
                        id_media: response.id_media,
                        id_destinationelement: response.id_destinationelement,
                        im_media: response.im_media,
                        en_elementtype: 0, ds_en_elementtype: '', ds_name: '', ds_shortname: '', vi_order: 0
                    };

                    if (!this.destination.ar_gallery) {
                        this.destination.ar_gallery = [];
                    }
                    this.destination.ar_gallery.push(newElement);
                    this.selectedGalleryImageIndex = this.destination.ar_gallery.length - 1;

                    if (this.isRotating) {
                        this.cancelRotation();
                    }

                    this._changeDetectorRef.markForCheck();
                }
            }
        });
    }

    addGalleryElement(file: File): void {
        const payload = new FormData();
        payload.append("im_image", file);
        payload.append("id_destination", this.destination.id_destination);

        this._spinner.show('managementSpinner').then();
        this._restFactoryService.httpPostService(this.API_POST_GALLERY_IMAGE, payload)
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => this._spinner.hide('managementSpinner').then())
            )
            .subscribe(response => {
                const newElement: GalleryType = {
                    id_media: response.id_media,
                    id_destinationelement: response.id_destinationelement,
                    im_media: response.im_media,
                    en_elementtype: 0, ds_en_elementtype: '', ds_name: '', ds_shortname: '', vi_order: 0
                };

                if (!this.destination.ar_gallery) {
                    this.destination.ar_gallery = [];
                }
                this.destination.ar_gallery.push(newElement);
                this.selectedGalleryImageIndex = this.destination.ar_gallery.length - 1;

                if (this.isRotating) {
                    this.cancelRotation();
                }

                this._changeDetectorRef.markForCheck();
            });
    }

    removeGalleryImage(): void {
        if (this.selectedGalleryImageIndex === null || !this.destination.ar_gallery || this.destination.ar_gallery.length === 0) {
            return;
        }

        const elementId = this.destination.ar_gallery[this.selectedGalleryImageIndex].id_destinationelement;
        const payload = new FormData();
        payload.append("id_element", elementId);
        payload.append("id_destination", this.destination.id_destination);

        this._restFactoryService.httpPostService(this.API_DELETE_GALLERY_IMAGE, payload, "image")
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.destination.ar_gallery.splice(this.selectedGalleryImageIndex, 1);

                if (this.destination.ar_gallery.length === 0) {
                    this.selectedGalleryImageIndex = null;
                } else if (this.selectedGalleryImageIndex >= this.destination.ar_gallery.length) {
                    this.selectedGalleryImageIndex = this.destination.ar_gallery.length - 1;
                }
                this._changeDetectorRef.markForCheck();
            });
    }

    setImageToBanner(type: 'main' | 'card'): void {
        if (this.selectedGalleryImageIndex === null || !this.destination.ar_gallery || this.destination.ar_gallery.length === 0) {
            return;
        }

        const mediaId = this.extractUUID(this.destination.ar_gallery[this.selectedGalleryImageIndex].id_media);
        if (!mediaId) return;

        const payload = new FormData();
        payload.append("id_media", mediaId);
        payload.append("id_destination", this.destination.id_destination);

        const api = type === 'card' ? this.API_POST_MAIN_CARD_IMAGE : this.API_POST_MAIN_IMAGE;
        const property = type === 'card' ? 'im_mediacard' : 'im_media';

        this._restFactoryService.httpPostService(api, payload, "image")
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.destination[property] = this.destination.ar_gallery[this.selectedGalleryImageIndex].im_media;
                this._changeDetectorRef.markForCheck();
            });
    }

    startRotationMode(): void {
        if (this.selectedGalleryImageIndex === null || !this.destination.ar_gallery) return;

        const currentImage = this.destination.ar_gallery[this.selectedGalleryImageIndex];
        const imageUrl = currentImage.im_media.replace('_tmb', '');

        this._spinner.show('managementSpinner').then();

        fetch(imageUrl)
            .then(res => res.blob())
            .then(blob => {
                this.rotationImageBlobUrl = URL.createObjectURL(blob);
                this.rotationDegree = 0;
                this.isRotating = true;
                this._changeDetectorRef.markForCheck();
            })
            .catch(() => {
                this.showError("Failed to load image for rotation.");
            })
            .finally(() => {
                this._spinner.hide('managementSpinner').then();
            });
    }

    rotateRight(): void {
        this.rotationDegree = (this.rotationDegree + 90) % 360;
    }

    rotateLeft(): void {
        this.rotationDegree = (this.rotationDegree - 90) % 360;
        if (this.rotationDegree < 0) this.rotationDegree += 360;
    }

    cancelRotation(): void {
        this.cleanupRotation();
        this.isRotating = false;
        this.rotationDegree = 0;
    }

    saveRotatedImage(): void {
        if (!this.rotationImageBlobUrl) return;

        if (this.rotationDegree === 0) {
            this.cancelRotation();
            return;
        }

        this._spinner.show('managementSpinner').then();

        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (this.rotationDegree === 90 || this.rotationDegree === 270) {
                canvas.width = img.height;
                canvas.height = img.width;
            } else {
                canvas.width = img.width;
                canvas.height = img.height;
            }

            ctx.translate(canvas.width / 2, canvas.height / 2);
            ctx.rotate(this.rotationDegree * Math.PI / 180);
            ctx.drawImage(img, -img.width / 2, -img.height / 2);

            canvas.toBlob((blob) => {
                if (blob) {
                    const file = new File([blob], `rotated_${Date.now()}.jpg`, {type: 'image/jpeg'});
                    this.addGalleryElement(file);
                } else {
                    this._spinner.hide('managementSpinner').then();
                    this.showError("Failed to process image.");
                }
            }, 'image/jpeg', 0.95);
        };
        img.onerror = () => {
            this._spinner.hide('managementSpinner').then();
            this.showError("Error processing image.");
        };
        img.src = this.rotationImageBlobUrl;
    }

    private cleanupRotation(): void {
        if (this.rotationImageBlobUrl) {
            URL.revokeObjectURL(this.rotationImageBlobUrl);
            this.rotationImageBlobUrl = null;
        }
    }

    private extractUUID(url: string): string | null {
        if (!url) return null;
        const regex = /([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/;
        const match = url.match(regex);
        return match ? match[0] : null;
    }

    private showError(message: string): void {
        const dialog = this._fuseConfirmationService.open({
            title: "Error!",
            message: message,
            actions: {confirm: {show: false}, cancel: {show: false}},
            dismissible: false
        });
        setTimeout(() => dialog.close(), 2000);
    }

    openInNewTab(url: string): void {
        if (!url) {
            return;
        }
        const cleanUrl = url.replace('_tmb', '');
        window.open(cleanUrl, '_blank');
    }
}
